import { Component, OnInit, ViewChild, ViewEncapsulation, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatSnackBar } from '@angular/material';
import { FuseUploadService } from '../upload.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { UploadOptions } from '../upload.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

@Component({
    selector: 'fuse-upload-dialog',
    templateUrl: './dialog.component.html',
    styleUrls: ['./dialog.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DialogComponent implements OnInit, OnDestroy {

    progress;
    canBeClosed = true;
    primaryButtonText = this._translate.instant('Upload');
    showCancelButton = true;
    uploading = false;
    uploadSuccessful = false;
    filesAdded = 0;
    options: UploadOptions;
    lang = 'sr';
    maxFileSizeLimit = 0;
    maxFileSize = '';
    maxFilesCount = 6;

    private _unsubscribeAll: Subject<any>;

    @ViewChild('file', { static: true }) file;
    public files: Set<File> = new Set();

    constructor(
        public dialogRef: MatDialogRef<DialogComponent>,
        public uploadService: FuseUploadService,
        @Inject(MAT_DIALOG_DATA) private _data: any,
        private _matSnackBar: MatSnackBar,
        private _translate: TranslateService,
        private _translationLoader: FuseTranslationLoaderService,
    ) {
        this.options = _data.options;
        this._unsubscribeAll = new Subject();

        this.maxFileSizeLimit = _data.options.maxFileSize;
        this.maxFileSize = this.maxFileSizeLimit > 1048576 ? Math.round(this.maxFileSizeLimit / 1048576) + 'MB' : Math.round(this.maxFileSizeLimit / 1024) + 'kB';
    }


    private getTranslations(): void {
        this._translationLoader.getLastLang()
            .then(response => {
                this.lang = response['code'];
                this._translationLoader.getTranslations(this.lang, '6');
            })
            .catch(err => Promise.reject(err))
            .then(() => {
            }, err => {
            });
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {

        this._translationLoader.onLangChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.getTranslations();
            });
        this.getTranslations();

    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    addFiles(): void {
        this.file.nativeElement.click();
    }

    onFilesAdded(): void {
        if (!this.options.multiple) {
            this.files = new Set();
        }

        const files: { [key: string]: File } = this.file.nativeElement.files;
        if (this.file.nativeElement.files.length <= this.maxFilesCount) {
            for (const key in files) {
                if (!isNaN(parseInt(key, 10))) {
                    if (this.maxFileSizeLimit && files[key].size <= this.maxFileSizeLimit) {
                        if (this.options.accept.indexOf(files[key].type) > -1 && files[key].type) {
                            this.files.add(files[key]);
                            this.filesAdded++;
                        } else {
                            this.fileTypeWarning();
                        }
                    } else if (!this.maxFileSizeLimit) {
                        if (this.options.accept.indexOf(files[key].type) > -1 && files[key].type) {
                            this.files.add(files[key]);
                            this.filesAdded++;
                        } else {
                            this.fileTypeWarning();
                        }
                    } else {
                        this.maxFileSizeWarning();
                    }
                }
            }
        } else {
            this.maxFileNumberWarning();
        }
    }

    maxFileSizeWarning(): void {
        this._matSnackBar.open(this._translate.instant('File can\'t be added. It is too large!'), '', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
            duration: 2000
        });
    }

    maxFileNumberWarning(): void {
        this._matSnackBar.open(this._translate.instant('Too many files! Max. allowed is ') + this.maxFilesCount, '', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
            duration: 2000
        });
    }

    fileTypeWarning(): void {
        this._matSnackBar.open(this._translate.instant('Selected file type is not allowed!'), '', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
            duration: 2000
        });
    }

    closeDialog(): void {
        // if everything was uploaded already, just close the dialog
        if (this.uploadSuccessful) {
            return this.dialogRef.close();
        }

        // set the component state to "uploading"
        this.uploading = true;

        // start the upload and save the progress map
        this.progress = this.uploadService.upload(this.files, this.options);

        // convert the progress map into an array
        const allProgressObservables = [];
        for (const key in this.progress) {
            if (this.progress.hasOwnProperty(key)) {
                allProgressObservables.push(this.progress[key].progress);
            }
        }

        // Adjust the state variables

        // The OK-button should have the text "Finish" now
        this.primaryButtonText = this._translate.instant('Finish');

        // The dialog should not be closed while uploading
        this.canBeClosed = false;
        this.dialogRef.disableClose = true;

        // Hide the cancel-button
        this.showCancelButton = false;

        // When all progress-observables are completed...
        forkJoin(allProgressObservables)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(end => {
                // ... the dialog can be closed again...
                this.canBeClosed = true;
                this.dialogRef.disableClose = false;

                // ... the upload was successful...
                this.uploadSuccessful = true;

                // ... and the component is no longer uploading
                this.uploading = false;

                // Show the success message
                this._matSnackBar.open(this._translate.instant('File(s) uploaded successfully.'), '', {
                    verticalPosition: 'top',
                    horizontalPosition: 'right',
                    duration: 2000
                });
            });
    }
}
